import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
    Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
    MenuItem, TextField, Typography,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import SftGlobalData from '../../library/SftGlobalData';
import { webshopErrorText } from './_webshopNotice';

// The shop URL without the mini-api suffix auth stores on display_name.
export const shopBaseUrl = (w) => String((w && (w.display_name || w.url)) || '')
    .trim().replace(/\/server\/v1\/mini-api\/?$/i, '').replace(/\/+$/, '');
const hostOf = (u) => { try { return new URL(u).host.replace(/^www\./i, '').toLowerCase(); } catch (e) { return ''; } };
const norm = (v) => String(v || '').trim().toLowerCase();
const RUNNING = ['queued', 'running'];
// Pause between two steps; every step itself works up to ~15 s on the shop.
const STEP_GAP_MS = 800;
// After a failed step (proxy timeout, 503 from a busy host) wait longer each
// time: 4, 8, 16, then 30 s; a step that succeeds resets it.
const RETRY_GAP_MS = 4000;
const RETRY_GAP_MAX_MS = 30000;

/**
 * Clone another webshop of the same client into the selected one (SoftTouch
 * SUPER only — the Actions screen only shows the card to them). The work runs
 * on the shop server (mini-api /admin/clone/*, lib/Clone/ShopCloner):
 * database, content files, then the URLs — in short steps, because the host
 * kills long PHP runs: while this dialog is open it calls
 * /admin/clone/continue one step after the other; closed, the clone waits and
 * goes on when the dialog opens again. Only between shops on the same
 * hosting account: checked here on the FTP host + user from Connecties and
 * again by the mini-api on disk. Passwords are never sent along.
 */
const CloneWebshopDialog = ({ open, onClose, t, api, login, target, webshops = [], notify }) => {
    const tr = (k, fb) => (typeof t === 'function' ? t(k, fb) : fb);
    const targetBase = shopBaseUrl(target);
    const targetHost = hostOf(targetBase);
    const sources = webshops.filter(w => w && target && w.id !== target.id && shopBaseUrl(w));

    const [sourceId, setSourceId] = useState('');
    const [ftp, setFtp] = useState({});          // webshop id -> ftp blob | null
    const [ftpError, setFtpError] = useState('');
    const [confirmText, setConfirmText] = useState('');
    const [status, setStatus] = useState(null);
    const [starting, setStarting] = useState(false);
    const [stopping, setStopping] = useState(false);
    const loopRef = useRef({ on: false, timer: null, fails: 0 });

    const proxy = useCallback((method, path, body) => SftGlobalData.post('webshop&action=proxy', {
        key: api, webshop_id: target?.id, sftUserId: login?.sft_user_id, user: login?.login, method, path, ...(body ? { body } : {}),
    }), [api, target?.id, login?.sft_user_id, login?.login]);

    const loadFtp = useCallback(async (id) => {
        if (!id || ftp[id] !== undefined) return;
        const r = await SftGlobalData.post('webshop&action=getCredentials', { key: api, webshop_id: id }).catch(() => null);
        if (!r || !r.success) setFtpError(r?.message || tr('cfg_act_clone_ftp_load_failed', 'Could not load the FTP details.'));
        setFtp(prev => ({ ...prev, [id]: (r && r.success && r.data && r.data.ftp) || null }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [api, ftp]);

    const fetchStatus = useCallback(async () => {
        const r = await proxy('GET', '/admin/clone/status').catch(() => null);
        if (!r || !r.success) return null;
        setStatus(r.data);
        return r.data;
    }, [proxy]);
    const stopPolling = () => {
        loopRef.current.on = false;
        if (loopRef.current.timer) { clearTimeout(loopRef.current.timer); loopRef.current.timer = null; }
    };
    // Drive the clone: one step after the other until it is done/failed or
    // the dialog closes. A failed call (proxy timeout, network) is followed
    // by a status read and another try a bit later.
    const startPolling = useCallback(() => {
        stopPolling();
        loopRef.current.on = true;
        loopRef.current.fails = 0;
        const step = async () => {
            if (!loopRef.current.on) return;
            const r = await proxy('POST', '/admin/clone/continue').catch(() => null);
            if (!loopRef.current.on) return;
            const s = (r && r.success) ? r.data : await fetchStatus();
            if (r && r.success) setStatus(s);
            if (s && !RUNNING.includes(s.state)) {
                stopPolling();
                if (notify) notify(s.state === 'done'
                    ? tr('cfg_act_clone_done', 'Clone finished.')
                    : tr('cfg_act_clone_failed', 'Clone failed — see the log.'), s.state === 'done' ? 2 : 1);
                return;
            }
            const okStep = !!(r && r.success);
            loopRef.current.fails = okStep ? 0 : loopRef.current.fails + 1;
            const gap = okStep
                ? (r.data?.busy ? RETRY_GAP_MS : STEP_GAP_MS)
                : Math.min(RETRY_GAP_MS * 2 ** (loopRef.current.fails - 1), RETRY_GAP_MAX_MS);
            loopRef.current.timer = setTimeout(step, gap);
        };
        step();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [proxy, fetchStatus, notify]);

    useEffect(() => {
        if (!open || !target) return undefined;
        setConfirmText('');
        setFtpError('');
        loadFtp(target.id);
        fetchStatus().then(s => { if (s && RUNNING.includes(s.state)) startPolling(); });
        return stopPolling;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, target?.id]);
    useEffect(() => { if (open && sourceId) loadFtp(sourceId); }, [open, sourceId, loadFtp]);

    const source = sources.find(w => String(w.id) === String(sourceId)) || null;
    const tf = target ? ftp[target.id] : undefined;
    const sf = source ? ftp[source.id] : undefined;
    const ftpReady = tf !== undefined && (!source || sf !== undefined);
    let accountCheck = null;   // null = not decidable yet
    if (source && ftpReady) {
        if (!tf || !sf || !tf.host || !sf.host) accountCheck = { ok: false, msg: tr('cfg_act_clone_ftp_missing', 'FTP details are missing in Connections for one of both webshops.') };
        else if (norm(tf.host) !== norm(sf.host) || norm(tf.username) !== norm(sf.username)) accountCheck = { ok: false, msg: tr('cfg_act_clone_other_account', 'These webshops are not on the same hosting account (different FTP host or user). Cloning only works within one account.') };
        else accountCheck = { ok: true, msg: tr('cfg_act_clone_same_account', 'Same hosting account — the clone can run on the server.') };
    }
    const running = !!status && RUNNING.includes(status.state);
    const confirmed = !!targetHost && norm(confirmText).replace(/^www\./, '') === targetHost;
    const canStart = !!source && !!accountCheck?.ok && confirmed && !running && !starting;

    const start = async () => {
        if (!canStart) return;
        setStarting(true);
        try {
            const r = await proxy('POST', '/admin/clone/start', {
                confirm:    true,
                source_dir: sf.directory || '',
                target_dir: tf.directory || '',
                source_url: shopBaseUrl(source),
                target_url: targetBase,
            });
            if (r && r.success) {
                setConfirmText('');
                await fetchStatus();
                startPolling();
                return;
            }
            const s = await fetchStatus();
            if (s && RUNNING.includes(s.state)) {
                setConfirmText('');
                startPolling();
            } else if (notify) {
                notify(`${tr('cfg_act_clone_title', 'Clone webshop')}: ${webshopErrorText(r, tr) || tr('cfg_act_failed', 'failed')}`, 1);
            }
        } catch (e) {
            const s = await fetchStatus();
            if (s && RUNNING.includes(s.state)) startPolling();
            else if (notify) notify(e?.message || tr('cfg_act_failed', 'failed'), 1);
        } finally {
            setStarting(false);
        }
    };

    const stop = async () => {
        setStopping(true);
        stopPolling();
        const r = await proxy('POST', '/admin/clone/cancel').catch(() => null);
        if (r && r.success) setStatus(r.data);
        const s = await fetchStatus();
        // a step that was running finishes its current file first
        if (s && RUNNING.includes(s.state)) startPolling();
        setStopping(false);
    };

    const stateChip = status && status.state !== 'idle' && (
        <Chip size="small"
            color={status.state === 'done' ? 'success' : status.state === 'failed' ? 'error' : 'info'}
            label={tr(`cfg_act_clone_state_${status.state}`, status.state)} />
    );

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3 } }} data-clone-dialog>
            <DialogTitle>
                <Typography variant="h6" component="span" sx={{ fontWeight: 600 }}>
                    {tr('cfg_act_clone_dialog_title', 'Clone a webshop into %host%').replace('%host%', targetHost || '?')}
                </Typography>
            </DialogTitle>
            <DialogContent dividers>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Alert severity="warning">
                        <strong>{tr('cfg_act_clone_warn_title', 'Everything of this webshop is replaced by the source:')}</strong>{' '}
                        {tr('cfg_act_clone_warn_body', 'the database (except the admin audit and the logs), the images, page and container designs, voucher layouts, translations, cookie.js, fonts and scripts. The client (index.php), config.php, robots.txt and .htaccess of this shop stay. Then the URLs are switched to this shop. Edit on the source: changes made here are gone with the next clone.')}
                    </Alert>
                    <TextField select size="small" label={tr('cfg_act_clone_source', 'Source webshop')} value={sourceId}
                        onChange={(e) => setSourceId(e.target.value)} disabled={running}
                        helperText={sources.length ? '' : tr('cfg_act_clone_no_sources', 'This client has no other webshop.')}>
                        {sources.map(w => <MenuItem key={w.id} value={String(w.id)}>{shopBaseUrl(w)}</MenuItem>)}
                    </TextField>
                    {source && !ftpReady && <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}><CircularProgress size={16} /><Typography variant="body2">{tr('cfg_act_clone_checking', 'Checking the hosting account…')}</Typography></Box>}
                    {ftpError && <Alert severity="error">{ftpError}</Alert>}
                    {accountCheck && <Alert severity={accountCheck.ok ? 'success' : 'error'}>{accountCheck.msg}</Alert>}
                    {source && accountCheck?.ok && (
                        <TextField size="small" fullWidth value={confirmText} onChange={(e) => setConfirmText(e.target.value)} disabled={running}
                            label={tr('cfg_act_clone_confirm_label', 'Type %host% to confirm').replace('%host%', targetHost)}
                            inputProps={{ 'data-clone-confirm': true, autoComplete: 'off' }} />
                    )}
                    {status && status.state !== 'idle' && (
                        <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5, p: 1.5 }} data-clone-status>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{tr('cfg_act_clone_last_run', 'Last clone')}</Typography>
                                {stateChip}
                                {running && <CircularProgress size={14} />}
                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                    {[status.source_url && `${status.source_url} → ${status.target_url}`, status.started_by, status.started_at && new Date(status.started_at).toLocaleString()].filter(Boolean).join(' · ')}
                                </Typography>
                            </Box>
                            {(status.steps || []).map((s, i) => (
                                <Box key={i} sx={{ display: 'flex', gap: 0.75, alignItems: 'flex-start', py: 0.25 }}>
                                    {s.ok ? <CheckCircleOutlineIcon color="success" sx={{ fontSize: 16, mt: '2px' }} /> : <ErrorOutlineIcon color="error" sx={{ fontSize: 16, mt: '2px' }} />}
                                    <Typography variant="body2" sx={{ fontSize: '0.82rem', wordBreak: 'break-word' }}>{s.msg}</Typography>
                                </Box>
                            ))}
                            {running && status.progress && (
                                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', pl: 2.75 }} data-clone-progress>{status.progress}</Typography>
                            )}
                            {running && (
                                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.75 }} data-clone-keep-open>
                                    {tr('cfg_act_clone_keep_open', 'The clone runs in short steps while this window is open. Closed, it pauses and continues when you open it again.')}
                                </Typography>
                            )}
                        </Box>
                    )}
                </Box>
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
                {running && (
                    <Button color="error" onClick={stop} disabled={stopping} sx={{ textTransform: 'none', mr: 'auto' }}
                        startIcon={stopping ? <CircularProgress size={14} color="inherit" /> : null}>
                        {tr('cfg_act_clone_cancel', 'Stop clone')}
                    </Button>
                )}
                <Button onClick={onClose} sx={{ textTransform: 'none' }}>{tr('cfg_act_close', 'Close')}</Button>
                <Button variant="contained" color="error" onClick={start} disabled={!canStart}
                    startIcon={starting ? <CircularProgress size={16} color="inherit" /> : null} sx={{ textTransform: 'none' }}>
                    {tr('cfg_act_clone_start', 'Start clone')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

CloneWebshopDialog.propTypes = {
    open: PropTypes.bool, onClose: PropTypes.func, t: PropTypes.func, api: PropTypes.string, login: PropTypes.object,
    target: PropTypes.object, webshops: PropTypes.array, notify: PropTypes.func,
};

export default CloneWebshopDialog;
